import {
  CTCSS_TONES,
  decodeLbcd,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 8
const CHANNEL_SIZE = 16
const DTCS_FLAG = 0x80
const DTCS_REVERSE_FLAG = 0x40

type H777Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  programCommand: string
  identities: readonly string[]
  channels: number
  channelOffset?: number
  memorySize?: number
  ranges?: readonly (readonly [number, number])[]
  preserveFlagsMask?: number
}

export type H777Profile = {
  protocol: 'h777'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  powerLevels: readonly ['Low', 'High']
  programCommand: readonly number[]
  identities: readonly (readonly number[])[]
  channelOffset: number
  memorySize: number
  ranges: readonly (readonly [number, number])[]
  preserveFlagsMask: number
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

function createProfile(spec: H777Spec): H777Profile {
  const memorySize = spec.memorySize ?? 0x03e0
  return {
    protocol: 'h777',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: [
      'USB',
      'Read + write',
      `${spec.channels} channels`,
      'No channel names',
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channels,
    nameLength: 0,
    powerLevels: ['Low', 'High'],
    programCommand: ascii(spec.programCommand),
    identities: spec.identities.map(ascii),
    channelOffset: spec.channelOffset ?? 0x10,
    memorySize,
    ranges: spec.ranges ?? [
      [0x0000, 0x0110],
      [0x0380, 0x03e0],
      [0x02b0, 0x02c0],
    ],
    preserveFlagsMask: spec.preserveFlagsMask ?? 0x03,
  }
}

const STANDARD_RANGES = [
  [0x0000, 0x0110],
  [0x0380, 0x03e0],
  [0x02b0, 0x02c0],
] as const

const STANDARD_SPECS: readonly H777Spec[] = [
  {
    id: 'baofeng-bf-888',
    vendor: 'Baofeng',
    model: 'BF-888',
    summary: 'Baofeng BF-888 and common compatible H777-style radios.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'retevis-h777',
    vendor: 'Retevis',
    model: 'H777',
    summary: 'Retevis H777 compact UHF business radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'retevis-h777-plus',
    vendor: 'Retevis',
    model: 'H777 Plus',
    summary: 'Retevis H777 Plus using the common H777 memory layout.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'baofeng-bf-m4',
    vendor: 'Baofeng',
    model: 'BF-M4',
    summary: 'Baofeng BF-M4 compact H777-compatible radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'maxtalker-mt-8s',
    vendor: 'MaxTalker',
    model: 'MT-8S',
    summary: 'MaxTalker MT-8S using the H777 channel map.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'radioddity-ga-2s',
    vendor: 'Radioddity',
    model: 'GA-2S',
    summary: 'Radioddity GA-2S compact UHF radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'maverick-ra-100',
    vendor: 'Maverick',
    model: 'RA-100',
    summary: 'Maverick RA-100 H777-compatible radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'baofeng-bf-1901',
    vendor: 'Baofeng',
    model: 'BF-1901',
    summary: 'Baofeng BF-1901 using the PWPG970 programming command.',
    programCommand: 'PWPG970',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'baofeng-bf-1904',
    vendor: 'Baofeng',
    model: 'BF-1904',
    summary: 'Baofeng BF-1904 ten-watt version of the BF-1901.',
    programCommand: 'PWPG970',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'baofeng-bf-1909',
    vendor: 'Baofeng',
    model: 'BF-1909',
    summary: 'Baofeng BF-1909 with extended UHF coverage.',
    programCommand: 'PWPG970',
    identities: ['P320h', 'P3107\u00f4AM'],
    channels: 16,
    memorySize: 0x03f0,
    ranges: [
      [0x0000, 0x0110],
      [0x0250, 0x0260],
      [0x02b0, 0x02c0],
      [0x03c0, 0x03e0],
    ],
  },
  {
    id: 'maverick-ra-425',
    vendor: 'Maverick',
    model: 'RA-425',
    summary: 'Maverick RA-425 ten-watt UHF radio.',
    programCommand: 'PWPG970',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'baofeng-mp31',
    vendor: 'Baofeng',
    model: 'MP31',
    summary: 'Baofeng MP31 with thirty-eight compact channel slots.',
    programCommand: 'PROGRAM',
    identities: ['P3107\u00f7\u0000\u0000'],
    channels: 38,
    channelOffset: 0,
    memorySize: 0x0400,
    ranges: [[0x0000, 0x0400]],
  },
  {
    id: 'zartek-za-758',
    vendor: 'Zartek',
    model: 'ZA-758',
    summary: 'Zartek ZA-758 with protected identity and DTMF blocks.',
    programCommand: 'LROGRAM',
    identities: ['SMP558\u0000\u0000'],
    channels: 16,
    memorySize: 0x0400,
    ranges: [
      [0x0000, 0x0110],
      [0x03a0, 0x03d0],
      [0x03e0, 0x03f0],
    ],
    preserveFlagsMask: 0xe3,
  },
  {
    id: 'arcshell-ar-5',
    vendor: 'Arcshell',
    model: 'AR-5',
    summary: 'Arcshell AR-5 H777-compatible compact radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'arcshell-ar-6',
    vendor: 'Arcshell',
    model: 'AR-6',
    summary: 'Arcshell AR-6 H777-compatible compact radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'greaval-gv-8s',
    vendor: 'Greaval',
    model: 'GV-8S',
    summary: 'Greaval GV-8S H777-compatible compact radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'greaval-gv-9s',
    vendor: 'Greaval',
    model: 'GV-9S',
    summary: 'Greaval GV-9S H777-compatible compact radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'ansoko-a-8s',
    vendor: 'Ansoko',
    model: 'A-8S',
    summary: 'Ansoko A-8S H777-compatible compact radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
  {
    id: 'tenway-tw-325',
    vendor: 'Tenway',
    model: 'TW-325',
    summary: 'Tenway TW-325 H777-compatible compact radio.',
    programCommand: 'PROGRAM',
    identities: ['P3107'],
    channels: 16,
  },
]

export const H777_PROFILES: readonly H777Profile[] = STANDARD_SPECS.map(
  createProfile,
)

function emptyChannel(
  number: number,
  profile: H777Profile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

export function decodeH777Bcd(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    const byte = raw[index]
    const high = byte >> 4
    const low = byte & 0x0f
    if (high > 9 || low > 9) return null
    value = value * 100 + high * 10 + low
  }
  return value
}

function encodeH777Bcd(value: number, length: number): Uint8Array {
  const result = new Uint8Array(length).fill(0xff)
  let remaining = value
  for (let index = 0; index < result.length; index += 1) {
    const part = remaining % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    remaining = Math.floor(remaining / 100)
  }
  return result
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (raw.every((value) => value === 0xff)) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  const isDtcs = (raw[1] & DTCS_FLAG) !== 0
  const polarity = (raw[1] & DTCS_REVERSE_FLAG) !== 0 ? 'R' : 'N'
  const masked = raw.slice()
  masked[1] &= 0x3f
  const value = decodeH777Bcd(masked)
  if (value === null || value === 0) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  return isDtcs
    ? { toneMode: 'dtcs', ctcss: null, dtcs: value, dtcsPolarity: polarity }
    : { toneMode: 'ctcss', ctcss: value / 10, dtcs: null, dtcsPolarity: 'N' }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Array.from(encodeH777Bcd(Math.round(channel.ctcss * 10), 2)) as [
      number,
      number,
    ]
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const raw = encodeH777Bcd(channel.dtcs, 2)
    raw[1] |= DTCS_FLAG
    if (channel.dtcsPolarity === 'R') raw[1] |= DTCS_REVERSE_FLAG
    return [raw[0], raw[1]]
  }
  return [0xff, 0xff]
}

export function parseH777Channel(
  image: Uint8Array,
  profile: H777Profile,
  number: number,
): WebChannel {
  const offset = profile.channelOffset + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (raw.byteLength !== CHANNEL_SIZE || !rxFrequency) {
    return emptyChannel(number, profile)
  }
  const flags = raw[12]
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...decodeTone(raw.slice(8, 10)),
    mode: (flags & 0x04) !== 0 ? 'NFM' : 'FM',
    power: (flags & 0x08) !== 0 ? 'High' : 'Low',
    skip: (flags & 0x10) !== 0,
  }
}

export function parseH777Channels(
  image: Uint8Array,
  profile: H777Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseH777Channel(image, profile, index + 1),
  )
}

const CTCSS_TENTHS = new Set(CTCSS_TONES.map((tone) => Math.round(tone * 10)))

export function applyH777Channel(
  image: Uint8Array,
  profile: H777Profile,
  channel: WebChannel,
): void {
  const offset = profile.channelOffset + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (
    channel.toneMode === 'ctcss' &&
    (!channel.ctcss || !CTCSS_TENTHS.has(Math.round(channel.ctcss * 10)))
  ) {
    throw new Error('That CTCSS tone is not supported by this radio.')
  }
  const preserve = raw[12] & profile.preserveFlagsMask
  raw.fill(0, 0, 12)
  const rx = encodeH777Bcd(channel.rxFrequency / 10, 4)
  raw.set(rx, 0)
  if (channel.txFrequency) {
    raw.set(encodeH777Bcd(channel.txFrequency / 10, 4), 4)
  } else {
    raw.fill(0xff, 4, 8)
  }
  const tone = encodeTone(channel)
  raw.set(tone, 8)
  raw.set(tone, 10)
  raw[12] =
    preserve |
    (channel.skip ? 0x10 : 0) |
    (channel.power === 'High' ? 0x08 : 0) |
    (channel.mode === 'NFM' ? 0x04 : 0)
  raw.fill(0xff, 13, 16)
}

function containsBytes(haystack: Uint8Array, needle: Uint8Array): boolean {
  for (let start = 0; start <= haystack.length - needle.length; start += 1) {
    if (needle.every((value, index) => haystack[start + index] === value)) {
      return true
    }
  }
  return false
}

function blockAddresses(profile: H777Profile): number[] {
  return Array.from(
    { length: Math.ceil(profile.memorySize / BLOCK_SIZE) },
    (_, index) => index * BLOCK_SIZE,
  )
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 2000))[0] !== ACK) {
    throw new Error(message)
  }
}

async function enterProgrammingMode(
  profile: H777Profile,
  transport: RadioTransport,
): Promise<string> {
  await transport.discardInput()
  await transport.write(Uint8Array.of(0x02))
  await new Promise((resolve) => setTimeout(resolve, 100))
  await transport.write(Uint8Array.from(profile.programCommand))
  await readAck(transport, 'The radio refused H777 programming mode.')
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 3000)
  if (
    !profile.identities.some((candidate) =>
      containsBytes(identity, Uint8Array.from(candidate)),
    )
  ) {
    throw new Error(
      `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
    )
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, 'The radio rejected the H777 programming session.')
  return new TextDecoder('ascii')
    .decode(identity)
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = Uint8Array.of(
    0x52,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
  await transport.write(command)
  const response = await transport.readExact(4 + BLOCK_SIZE, 2000)
  if (compareImages(command, response.slice(0, 4)) !== null) {
    throw new Error(`Unexpected H777 read reply at 0x${address.toString(16)}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, `The radio rejected read block 0x${address.toString(16)}.`)
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const command = new Uint8Array(4 + BLOCK_SIZE)
  command.set(
    Uint8Array.of(
      0x57,
      (address >> 8) & 0xff,
      address & 0xff,
      BLOCK_SIZE,
    ),
  )
  command.set(data, 4)
  await transport.write(command)
  await readAck(transport, `The radio rejected write block 0x${address.toString(16)}.`)
}

export async function readH777RadioWithTransport(
  profile: H777Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(profile.memorySize).fill(0xff)
    const addresses = blockAddresses(profile)
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Reading H777-family memory',
      })
    }
    return { image, reportedModel: reportedModel || profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeH777RadioWithTransport(
  profile: H777Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this radio memory map.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const addresses = profile.ranges.flatMap(([start, end]) =>
      Array.from(
        { length: Math.ceil((end - start) / BLOCK_SIZE) },
        (_, index) => start + index * BLOCK_SIZE,
      ),
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing H777-family memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
